import { MATRIX_VOIP_PROTO_VERSION } from "./callRinging";

export const MATRIX_CALL_ESCALATE_EVENT_TYPE = "org.phonehq.call.escalate" as const;

export const CALL_ESCALATION_LIFETIME_MS = 60_000;

const MAX_CLOCK_SKEW_MS = 5 * 60_000;

export type CallEscalationIntent = "audio" | "video";

export interface MatrixCallEscalationContent {
    readonly call_id: string;
    readonly room_id: string;
    readonly intent: CallEscalationIntent;
    readonly expires_ts: number;
    readonly version: typeof MATRIX_VOIP_PROTO_VERSION;
}

export const buildCallEscalationContent = (escalation: {
    callId: string;
    roomId: string;
    intent: CallEscalationIntent;
    now: number;
}): MatrixCallEscalationContent => ({
    call_id: escalation.callId,
    room_id: escalation.roomId,
    intent: escalation.intent,
    expires_ts: escalation.now + CALL_ESCALATION_LIFETIME_MS,
    version: MATRIX_VOIP_PROTO_VERSION,
});

export const isValidCallEscalationContent = (content: unknown): content is MatrixCallEscalationContent => {
    if (typeof content !== "object" || content === null) return false;
    const { call_id: callId, room_id: roomId, intent, expires_ts: expiresTs, version } = content as Record<string, unknown>;
    return typeof callId === "string" && callId.length > 0
        && typeof roomId === "string" && roomId.startsWith("!")
        && (intent === "audio" || intent === "video")
        && typeof expiresTs === "number" && Number.isFinite(expiresTs)
        && version === MATRIX_VOIP_PROTO_VERSION;
};

export interface ActiveDirectCall {
    readonly matrixCallId: string;
    readonly roomId: string;
    readonly remoteUserId?: string;
    readonly answered: boolean;
}

export type CallEscalationDecision =
    | { readonly kind: "follow"; readonly content: MatrixCallEscalationContent }
    | { readonly kind: "ignore"; readonly reason: "invalid" | "own" | "expired" | "no_call" | "other_call" | "not_peer" | "not_answered" };

export const decideCallEscalation = (input: {
    readonly content: unknown;
    readonly senderId: string;
    readonly roomId: string;
    readonly localUserId: string | null;
    readonly active: ActiveDirectCall | undefined;
    readonly now: number;
}): CallEscalationDecision => {
    const { content, senderId, roomId, localUserId, active, now } = input;
    if (!isValidCallEscalationContent(content)) {
        return { kind: "ignore", reason: "invalid" };
    }
    if (!localUserId || senderId === localUserId) {
        return { kind: "ignore", reason: "own" };
    }
    if (content.expires_ts <= now || content.expires_ts > now + CALL_ESCALATION_LIFETIME_MS + MAX_CLOCK_SKEW_MS) {
        return { kind: "ignore", reason: "expired" };
    }
    if (!active) {
        return { kind: "ignore", reason: "no_call" };
    }
    if (active.matrixCallId !== content.call_id || active.roomId !== roomId || content.room_id === active.roomId) {
        return { kind: "ignore", reason: "other_call" };
    }
    if (active.remoteUserId !== senderId) {
        return { kind: "ignore", reason: "not_peer" };
    }
    if (!active.answered) {
        return { kind: "ignore", reason: "not_answered" };
    }
    return { kind: "follow", content };
};
